<script module>
  export const i18n = {
    sv: {
      'Preparing results': 'Förbereder resultat',
      'Analyzing routes': 'Analyserar rutter',
      'Checking timetables': 'Kontrollerar tider',
      'Verifying timetables': 'Verifierar tider',
      'Getting train data': 'Hämtar tågdata',
      'Still working': 'Jobbar'
    }
  }
</script>

<script>
  import { cubicIn, cubicOut } from 'svelte/easing'
  import { fade, fly } from 'svelte/transition'
  import { onMount } from 'svelte'

  import { register } from '$lib/utils/text.svelte.js'

  let text = register(i18n)

  /** @type {{ end?: boolean, children: Svelte.Snippet }} */
  let { end = false, children } = $props()

  let ending = $state(false)
  let ended = $state(false)
  let spinner = $state()
  let iteration = $state(0)

  function stop() {
    // Wait for spin iteration
    spinner.addEventListener('animationiteration', function () {
      ending = true
      // Wait for ending animation
      spinner.addEventListener('animationend', function () {
        ended = true
      })
    })
  }

  function iterate() {
    iteration++
    setTimeout(iterate, 2000)
  }

  onMount(() => {
    if (end) {
      ended = true
    }
    iterate()
  })

  $effect(() => {
    if (end && !ended) {
      stop()
    }
  })
</script>

{#if ended}
  <div in:fly={{ y: '3rem', duration: 300, delay: 200, easing: cubicOut }}>
    {@render children()}
  </div>
{:else}
  <div
    class="splash splash-{iteration}"
    class:iterated={iteration > 4}
    class:ending
    class:end
    out:fade={{ duration: 200, easing: cubicIn }}>
    <div class="figure">
      <svg viewBox="0 0 600 600" aria-hidden="true">
        <g fill="none" fill-rule="evenodd" class="spinner" bind:this={spinner}>
          <g class="part part-left">
            <path
              fill="var(--blue-1)"
              class="line line-left"
              d="M233.1 210.3a21.9 21.9 0 0127.6-14h.2a21.9 21.9 0 0114 27.6l-54 166.4a21.9 21.9 0 01-27.5 14h-.3a21.9 21.9 0 01-14-27.6l54-166.4zM217.8 300a9 9 0 1018 0 9 9 0 00-18 0zm28.5-83.7a8 8 0 1016 0 8 8 0 00-16 0zM191.2 384a8 8 0 1016 0 8 8 0 00-16 0z" />
          </g>
          <g class="part part-middle">
            <path
              fill="var(--yellow)"
              class="line line-middle"
              d="M224.5 278.4l137.5-15c12-1.3 22.8 7.3 24 19.3l.1.3c1.3 12-7.3 22.8-19.4 24.1l-137.4 15a21.9 21.9 0 01-24.1-19.3v-.2a21.9 21.9 0 0119.3-24.2zm135 5.5a8 8 0 1016 0 8 8 0 00-16 0zm-69.6 9a8 8 0 1016 0 8 8 0 00-16 0zm-71 7.1a8 8 0 1016 0 8 8 0 00-16 0z" />
          </g>
          <g class="part part-right">
            <path
              fill="var(--blue-2)"
              class="line line-right"
              d="M366.4 210.3a21.9 21.9 0 00-27.6-14h-.2a21.9 21.9 0 00-14 27.6L379 391.5a21.9 21.9 0 0027.6 14h.2a21.9 21.9 0 0014-27.6l-54.4-167.6zM387 334.7a9 9 0 110 18 9 9 0 010-18zm-41.7-126.4a8 8 0 110 16 8 8 0 010-16zm22.1 67.6a8 8 0 110 16 8 8 0 010-16z" />
          </g>
          <g class="part part-bottom">
            <path
              fill="var(--red)"
              class="line line-bottom"
              d="M408.1 339.2c2.6 11.8-4.9 23.5-16.7 26l-186.9 40.6a21.9 21.9 0 11-9.3-42.7l187-40.6c11.7-2.6 23.4 5 26 16.7zm-218 45a9 9 0 1018 0 9 9 0 00-18 0zm99.8-20.6a8 8 0 1016 0 8 8 0 00-16 0zm89-19.9a8 8 0 1016 0 8 8 0 00-16 0z" />
          </g>
          <g class="part part-left">
            <path
              fill="var(--blue-1)"
              class="line line-left"
              d="M189 346.1l.2-.5 7-21.4 41.8 13.6-17 52.5-.5 1.2v-.1a22 22 0 01-27.6 14h-.2a21.9 21.9 0 01-14-27.6l10.3-31.7zm2 38a8 8 0 1016 0 8 8 0 00-16 0z" />
          </g>
        </g>
      </svg>
      <div class="body">
        {#if end}
          {text`Preparing results`}
        {:else if iteration === 1}
          {text`Analyzing routes`}
        {:else if iteration === 2}
          {text`Checking timetables`}
        {:else if iteration === 3}
          {text`Verifying timetables`}
        {:else if iteration === 4}
          {text`Getting train data`}
        {:else}
          {text`Still working`}
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .splash {
    --spinning-speed: 2000ms;
    --ending-speed: 800ms;
    --ease-out: cubic-bezier(0.29, 0.92, 0.34, 1);

    --blue-1: #a9aec3;
    --blue-2: #a9aec3;
    --yellow: #a9aec3;
    --red: #a9aec3;

    position: relative;
    display: flex;
    margin: 1rem 0 3rem;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  .splash-2 {
    --blue-1: rgb(var(--rgb-blue));
  }

  .splash-3 {
    --blue-1: rgb(var(--rgb-blue));
    --blue-2: rgb(var(--rgb-blue));
  }

  .splash-4 {
    --blue-1: rgb(var(--rgb-blue));
    --blue-2: rgb(var(--rgb-blue));
    --yellow: rgb(var(--rgb-yellow));
  }

  .splash-5,
  .splash.iterated,
  .splash.ending,
  .splash.end {
    --blue-1: rgb(var(--rgb-blue));
    --blue-2: rgb(var(--rgb-blue));
    --yellow: rgb(var(--rgb-yellow));
    --red: rgb(var(--rgb-red));
  }

  .figure {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    position: relative;
    z-index: 1;
  }

  /* Spinner */

  svg {
    max-width: 12rem;
    width: 100%;
    height: auto;
    transition: opacity var(--ending-speed) var(--ease);
  }

  .splash.ending svg {
    opacity: 1;
  }

  .spinner {
    animation: spinning var(--spinning-speed) linear infinite;
    transform-origin: center;
    will-change: transform;
  }

  @keyframes spinning {
    from {
      transform: rotate(-90deg);
    }

    to {
      transform: rotate(270deg);
    }
  }

  .ending .spinner {
    animation: ending var(--ending-speed) var(--ease-out) forwards;
  }

  @keyframes ending {
    from {
      transform: rotate(-90deg);
    }

    to {
      transform: rotate(0deg);
    }
  }

  .part {
    transform-origin: center;
    will-change: transform;
    transition: transform var(--ending-speed) var(--ease-out);
  }

  /**
   * Because of an renderings issue in Safari, the .ending stuff can't be
   * shortened into one selector.
   */

  .part-middle {
    transform: translateY(-32%) rotate(12deg);
  }

  .part-right {
    transform: translateX(17%);
  }

  .part-bottom {
    transform: translateY(24%) rotate(4deg);
  }

  .part-left {
    transform: translateX(-18%);
  }

  .ending .part-middle {
    transform: translateY(0%) rotate(0deg);
  }

  .ending .part-right {
    transform: translateX(0%);
  }

  .ending .part-bottom {
    transform: translateY(0%) rotate(0deg);
  }

  .ending .part-left {
    transform: translateX(0%);
  }

  .line {
    transition: 300ms var(--ease);
    transition-property: fill, color;
    transform-origin: center;
    will-change: transform;
  }

  .ending .line {
    transition: var(--ending-speed) var(--ease);
    transition-property: fill, color;
  }

  .line-left {
    transform-origin: 37.8% 50%;
    animation: line-spinning var(--spinning-speed) linear infinite;
  }

  .line-right {
    transform-origin: 61.2% 47.3%;
    animation: line-spinning var(--spinning-speed) linear infinite;
  }

  @keyframes line-spinning {
    to {
      transform: rotate(-720deg);
    }
  }

  .ending .line {
    animation: none;
  }

  .body {
    font-size: 1rem;
    color: rgb(var(--rgb-gray));
  }
</style>
